import { useCallback, useMemo } from "react";

import { usePostHog } from "posthog-js/react";

import {
  COMMAND_LINK_ANALYTICS_SOURCE,
  SubmitQuoteCommandLinkAction,
} from "./constants";
import { dispatchSubmitQuoteExecuteResult } from "./utils";
import { ClientAppEvent, CommandLinkName } from "../../../../constants";
import { useCommandLinkExecute } from "../../hooks/useCommandLinkExecute";

import type { SubmitQuoteExecuteResponse, SubmitQuoteParams } from "./types";

interface SubmitQuoteExecuteParams extends SubmitQuoteParams {
  action: typeof SubmitQuoteCommandLinkAction.SUBMIT_QUOTE;
}

interface UseSubmitQuoteCommandLinkProps {
  token: string;
  shipmentId: number;
  quoteType: string;
  params: SubmitQuoteParams;
  onSubmitted: VoidFunction;
  onRecoverableFailure: (outcomeCode: string | null) => void;
  onBlockedFailure: (blockedReasonCode: string) => void;
}

interface UseSubmitQuoteCommandLinkResult {
  submitQuote: VoidFunction;
  loading: boolean;
  error: string | null;
}

/**
 * Submits through the generic command-link execute endpoint and separates the two
 * kinds of failure it can report: a transport/hard error surfaces as `error`,
 * while a recoverable rejection comes back HTTP 200 with `success: false` and a
 * code the landing renders (ACTIVE_QUOTE_EXISTS sends the carrier to the app)
 * (docs/engineering/design/carrier_submit_quote_command_link_design.md §6).
 */
export function useSubmitQuoteCommandLink(
  props: UseSubmitQuoteCommandLinkProps,
): UseSubmitQuoteCommandLinkResult {
  const {
    token,
    shipmentId,
    quoteType,
    params,
    onSubmitted,
    onRecoverableFailure,
    onBlockedFailure,
  } = props;
  const posthog = usePostHog();

  const executeParams = useMemo<SubmitQuoteExecuteParams>(
    () => ({ ...params, action: SubmitQuoteCommandLinkAction.SUBMIT_QUOTE }),
    [params],
  );

  const {
    execute,
    loading,
    error: executeError,
  } = useCommandLinkExecute<
    SubmitQuoteExecuteResponse,
    SubmitQuoteExecuteParams
  >({
    commandName: CommandLinkName.SUBMIT_QUOTE,
    token,
    params: executeParams,
  });

  const submitQuote = useCallback(() => {
    execute((response) => {
      dispatchSubmitQuoteExecuteResult({
        response,
        onSuccess: () => {
          // The shared conversion event the in-app forms capture, so command-link
          // quotes land in the funnel product already watches
          // (docs/engineering/design/carrier_submit_quote_command_link_design.md §8).
          posthog?.capture(ClientAppEvent.QUOTE_SUBMITTED, {
            quote_type: quoteType,
            shipment_id: shipmentId,
            source: COMMAND_LINK_ANALYTICS_SOURCE,
          });
          onSubmitted();
        },
        /**
         * A stop the carrier cannot fix from the form — the shipment was
         * cancelled, booked or expired since the page loaded, or their own
         * account state changed. Reported alongside the recoverable codes rather
         * than as an empty 403, so the landing can name it instead of falling
         * back to its generic error copy (#2975 §2).
         */
        onBlockedFailure: (blockedReasonCode) => {
          onBlockedFailure(blockedReasonCode);
          posthog?.capture(ClientAppEvent.QUOTE_COMMAND_LINK_SUBMIT_FAILED, {
            shipment_id: shipmentId,
            outcome_code: blockedReasonCode,
            blocked: true,
          });
        },
        onRecoverableFailure: (outcomeCode) => {
          onRecoverableFailure(outcomeCode);
          posthog?.capture(ClientAppEvent.QUOTE_COMMAND_LINK_SUBMIT_FAILED, {
            shipment_id: shipmentId,
            outcome_code: outcomeCode,
            blocked: false,
          });
        },
      });
    });
  }, [
    execute,
    onSubmitted,
    onRecoverableFailure,
    onBlockedFailure,
    posthog,
    shipmentId,
    quoteType,
  ]);

  return { submitQuote, loading, error: executeError };
}
